import { ref, computed } from 'vue';
import { useRouter } from 'vue-router';

import { User, AppConfigResponse } from '@/models/auth';

import { AuthService } from '@/services/AuthService';
import { StorageService } from "@/services/storageService.ts";
import { apiFetch } from "@/services/ApiClient.ts";
import SendCommon from "@/services/IframeBridge/common/outbound.ts";

import { isNative, platform } from '@/utils/platform';

import { initNotificationStore } from '@/stores/notificationStore';

export const user = ref<User | null>(null);
export const isClockedIn = ref(false);
export const dailyCode = ref<string | null>(null);

export const clearUserSession = () => {
    user.value = null;
};

const loading = ref(true);
const refreshingSession = ref(false);
const showSessionModal = ref(false);
const sessionExpiryData = ref<any>(null);
const timeLeft = ref(0);
const modalType = ref<string | null>(null);
const lastErrorKey = ref<string | null>(null);
const chainServerUrl = ref<string | null>(null);

export function useAuth() {
    const router = useRouter();
    const errorMessage = ref('');
    const isLoading = ref(false);

    const isLoggedIn = computed(() => !!user.value);
    const hasChainServerUrl = computed(() => !!chainServerUrl.value && chainServerUrl.value !== "");

    const userData = computed<User>(() => {
        return user.value || {
            Id: '',
            Email: '',
            FirstName: '',
            LastName: '',
            Services: [],
            RecentServices: [],
            TokenExp: 0,
            Roles: [],
        } as User;
    });

    const setError = (err: any) => {
        const key = err.error?.msg || err.error || 'generic_error';
        errorMessage.value = `auth.errors.${key}`;
    };

    const initAuth = async () => {
        loading.value = true;
        try {
            const [session, permissionResult] = await Promise.all([
                AuthService.findSession(),
                StorageService.Get("CHAIN_SERVICER_URL"),
                initNotificationStore()
            ]);

            user.value = session?.user || null;
            chainServerUrl.value = permissionResult;

        } catch (error) {
            console.error("Auth init failed", error);
            user.value = null;
        } finally {
            loading.value = false;
        }
    };

    const handleLogin = async (code: string | null, state: string | null, redir: string | null, provider: string) => {
        errorMessage.value = '';
        isLoading.value = true;
        try {
            let reqPlatform = 'web'
            if (isNative) {
                reqPlatform = platform
            }
            const payload = { code, state, redir, "platform": reqPlatform, provider };
            const response = await AuthService.loginRequest(payload);
            if (response.success) {
                const data = response.data;

                if (data.authUrl === '') {
                    await StorageService.Set("IS_LOGGED_IN", true)
                }
                if (data.token !== '') {
                  if (isNative) {
                    await StorageService.Set("ACCESS_TOKEN", data.token)
                  } else {
                    sessionStorage.setItem('iframe_active_token', data.token);
                  }
                }

                return data
            } else {
                setError(response.error);
                return { 'error': response.error.msg }
            }
        } catch (err: any) {
            setError(err);
            return null;
        } finally {
            isLoading.value = false;
        }
    };

    /**
     * Clears local session and stops background services
     */
    const logout = async (redirectAfterLogin: string | null = null, errorMsg: string | null = null) => {
        try {
            try {
                const { useServiceStore } = await import('@/stores/useServiceStore');
                const serviceStore = useServiceStore();
                if (serviceStore && typeof serviceStore.selectService === 'function') {
                    serviceStore.selectService(null as any);
                }
            } catch (storeErr) {
                console.warn("Could not reset serviceStore during logout:", storeErr);
            }
            await AuthService.logout();
            user.value = null;

            const queryParams: Record<string, string> = {};
            if (redirectAfterLogin) {
                queryParams.redir = redirectAfterLogin;
            }
            if (errorMsg) {
                queryParams.err = errorMsg;
            }

            window.history.replaceState({}, '');
            if (router) {
                await router.replace({
                    path: '/login',
                    query: queryParams,
                    state: { token: null, status: null }
                });
            } else {
                const queryString = new URLSearchParams(queryParams).toString();
                const targetUrl = queryString ? `/login?${queryString}` : '/login';
                window.location.replace(targetUrl);
            }

        } catch (err) {
            console.error("Logout error:", err);
        } finally {
            isLoading.value = false;
        }
    };

    const setAuthAppConf = async (config: AppConfigResponse) => {
        const serverUrl = config.chainServerUrl;
        const locationUrl = config.locationServiceUrl;

        if (serverUrl) {
            await StorageService.Set("CHAIN_SERVICER_URL", serverUrl)
            chainServerUrl.value = serverUrl;
        }

        if (locationUrl) {
            await StorageService.Set("LOCATION_SERVICE_URL", locationUrl)
        }
    };

    const setUser = (newUser: User | null) => {
        user.value = newUser;
    };

    const triggerSessionWarning = (data: any) => {
        timeLeft.value = Math.round(data.timeLeft);
        modalType.value = data.modalType;
        lastErrorKey.value = null;
        if (!showSessionModal.value) {
            showSessionModal.value = true;
        }
    };

    const closeSessionWarning = () => {
        showSessionModal.value = false;
        timeLeft.value = 0;
    };

    const getAttendanceStatus = async () => {
        const response = await AuthService.isClockedInRequest()
        if (response.data) {
            updateAttendanceStatus(response.data)
        }
    }

    const updateAttendanceStatus = (payload: { status?: string; pinCode?: string | null }) => {
        if (payload?.status) {
            isClockedIn.value = (payload.status === 'in');
        }
        if (payload?.pinCode) {
            dailyCode.value = isClockedIn.value ? payload.pinCode : null;
        }
    }

    const executeTokenRefresh = async () => {
        try {
            const response = await apiFetch('/refreshToken', {
                method: 'POST',
                body: JSON.stringify({'platform': isNative})
            });

            if (!response.ok) {
                throw new Error(`Token refresh failed with status: ${response.status}`);
            }

            const resp = await response.json();
            const newToken = resp.data

            if (newToken) {
                sessionStorage.setItem('iframe_active_token', newToken);
                SendCommon.tokenRefreshed({ token: newToken }, { sendToAllIframes: true });
                if (isNative) {
                    await StorageService.Set("ACCESS_TOKEN", newToken);
                }
            }

            await initAuth();
            return {
                id: user.value?.Id || null,
                tokenExp: user.value?.TokenExp || null
            };
        } catch (error) {
            console.error("Auth: Token refresh process encountered an error", error);
            throw error;
        }
    };

    const executeRefreshSession = async (isApproval: boolean): Promise<{ success: boolean; errorKey?: string }> => {
        refreshingSession.value = true;
        try {
            const response = await apiFetch('/refreshSession', {
                method: 'POST',
                body: JSON.stringify({ isApproval: isApproval })
            });

            if (!response.ok) {
                let errorKey = 'generic_error';
                try {
                    const errorData = await response.json();
                    errorKey = errorData?.error?.msg || errorData?.msg || 'generic_error';
                } catch {
                    console.warn('executeRefreshSession - error getting response as json')
                }
                return { success: false, errorKey };
            }

            await response.json();
            return { success: true };

        } catch (error) {
            console.error('useAuth: executeRefreshSession connection error', error);
            return { success: false, errorKey: 'generic_error' };
        } finally {
            refreshingSession.value = false;
            showSessionModal.value = false;
        }
    };

    return {
        // State
        user,
        userData,
        isLoggedIn,
        loading,
        isLoading,
        errorMessage,
        showSessionModal,
        sessionExpiryData,
        timeLeft,
        modalType,
        lastErrorKey,
        chainServerUrl,
        hasChainServerUrl,
        isClockedIn,
        dailyCode,
        refreshingSession,

        // Methods
        setError,
        initAuth,
        handleLogin,
        logout,
        setAuthAppConf,
        setUser,
        triggerSessionWarning,
        closeSessionWarning,
        updateAttendanceStatus,
        getAttendanceStatus,
        executeTokenRefresh,
        executeRefreshSession
    };
}